<template>
    <v-container fluid class="pa-0">
        <v-card v-if="registrySchema != null">
            <v-card-title primary-title>
                <v-row>
                    <v-col>
                        {{ registrySchema.displayName }} ({{ registrySchema.name }}) Entries
                    </v-col>
                    <v-spacer />
                    <v-col class="text-right">
                        <v-btn color="primary" @click="viewSchema" class="mr-3">View Schema</v-btn>
                        <v-btn color="primary" @click="downloadRegistryEntriesTemplate" class="mr-3">Download Entries</v-btn>
                        <v-btn
                            color="primary"
                            @click="showImportRegistryEntries"
                            v-if="!registrySchema.isBenchling && userCanManageRegistryEntries"
                        >
                            Import</v-btn
                        >
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-subtitle>
                id {{ registrySchema.id }} /
                {{ registrySchema.isBenchling ? "Benchling" : "Invaio" }}
            </v-card-subtitle>
            <v-card-text>
                <v-checkbox label="Include archived entries" v-model="includeArchivedEntries" color="primary"></v-checkbox>
                <v-data-table
                    :headers="registryEntryHeaders"
                    :items="registryEntryTableItems"
                    item-key="id"
                    class="elevation-1"
                    :items-per-page="25"
                    :search="search"
                    :row-props="setRowStyle"
                >
                    <template v-slot:item.archive="{ item }">
                        <v-icon-btn v-if="item.archivedDatetime"
                                    @click="unArchiveRegistryEntry(item)"><v-icon>$archive-remove</v-icon></v-icon-btn>
                        <v-icon-btn v-else @click="archiveRegistryEntry(item)"><v-icon>$archive</v-icon></v-icon-btn>
                    </template>
                    <template v-slot:item.delete="{ item }">
                        <v-icon-btn @click="deleteRegistryEntry(item)"><v-icon>$delete</v-icon></v-icon-btn>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-dialog v-model="importRegistryEntriesDialogVisible">
                <v-card>
                    <v-card-title>Import {{ registrySchema.name }} Entries</v-card-title>
                    <v-card-text>
                        <v-file-input
                            v-model="registryEntriesFile"
                            label="Excel File"
                            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            style="width: 320px"
                        />
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            color="primary"
                            :disabled="registryEntriesFile == null"
                            @click="importRegistryEntriesFile"
                            >Save</v-btn
                        >
                        <v-btn color="warning" @click="closeImport">Cancel</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </v-container>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { VDataTableHeader } from "@/vuetifyTypeFixes";

import { downloadFile } from "@/lib/downloadFileUtil";
import { withLoadingScreen } from "@/lib/loading";
import { registriesAPI, RegistryEntry, SavedRegistrySchema } from "@/services/registries/registriesAPI";
import { useAuth } from "@/lib/ts/auth";
import store from "@/store";
import { MeasurementValueCoercer } from "@/services/measurements/measurementValueCoercer";
import _ from "lodash";

const route = useRoute();
const auth = useAuth();
const router = useRouter();

const search = computed(() => store.state.globalSearch ?? undefined);

type RegistryEntryTableItem = RegistryEntry & Record<string, unknown>;

const registrySchema = ref<SavedRegistrySchema>();
const registryEntryTableItems = ref<RegistryEntryTableItem[]>([]);
const registryEntryHeaders = ref<VDataTableHeader[]>([
    {
        title: "ID",
        value: "id",
        sortable: true,
        align: "start",
        width: "5%",
    },
    {
        title: "Name",
        value: "displayName",
        sortable: true,
        align: "start",
        width: "10%",
    },
]);

const includeArchivedEntries = ref(false);
const importRegistryEntriesDialogVisible = ref(false);
const registryEntriesFile = ref<File>();
const measurementValueCoercer = ref<MeasurementValueCoercer>();

const userCanManageRegistryEntries = computed(() => {
    return auth.isAuthorized("Registries:Manage Entries");
});

const registrySchemaName = route.params.name;

watch(includeArchivedEntries, async () => {
    await reloadEntries();
})

withLoadingScreen(async () => {
    const allSchemas = await registriesAPI.fetchAllRegistrySchemas();
    registrySchema.value = allSchemas.find(
        (registrySchema) => registrySchema.name === registrySchemaName
    );
    measurementValueCoercer.value = MeasurementValueCoercer.buildForRegistrySchema(registrySchema.value!);

    buildTableHeadersForSchemaAttributes();
    await reloadEntries();
});

function setRowStyle(params: { item: RegistryEntry }) {
    const { item } = params;
    if (!_.isNil(item.archivedDatetime)) {
        return { class: "archived" }
    }

    return {};
}

function viewSchema() {
    router.push({ path: `/registries/view/${registrySchema.value!.name}` });
}

async function reloadEntries() {
    await withLoadingScreen(async () => {
        const registryEntries = await registriesAPI.fetchRegistryEntriesForSchemaName(
            registrySchema.value!.name, {
                includeArchivedEntries: includeArchivedEntries.value
            }
        );
        buildTable(registryEntries);
    })
}

function buildTable(registryEntries: RegistryEntry[]) {
    registryEntryTableItems.value = registryEntries.map((registryEntry) => {
        const item = {
            ...registryEntry,
        };

        for (const attribute of registrySchema.value!.attributes) {
            const attributeName = attribute.name;
            const attributeValue = registryEntry.data[attribute.name];
            item[attributeName] = measurementValueCoercer.value!.getMeasurementDisplayValue(attribute, attributeValue);
        }
        return item;
    });
}

function buildTableHeadersForSchemaAttributes() {
    for (const attribute of registrySchema.value!.attributes) {
        registryEntryHeaders.value.push({
            title: attribute.display_name,
            value: attribute.name,
            sortable: true,
            width: "5%",
        });
    }

    if (registrySchema.value?.isBenchling) {
        registryEntryHeaders.value.push({
            title: "Benchling ID",
            value: "benchlingId",
            sortable: true,
            align: "start",
            width: "10%",
        })
    }

    if (!registrySchema.value!.isBenchling && userCanManageRegistryEntries) {
        registryEntryHeaders.value.push({
            title: "Archive",
            value: "archive",
            sortable: false,
            width: "1%",
        });

        registryEntryHeaders.value.push({
            title: "Delete",
            value: "delete",
            sortable: false,
            width: "1%",
        });
    }
}

async function downloadRegistryEntriesTemplate() {
    const response = await registriesAPI.downloadRegistryEntriesTemplateForSchemaName(
        registrySchema.value!.name
    );
    downloadFile(`${registrySchema.value!.name}-entries.xlsx`, response);
}

function showImportRegistryEntries() {
    importRegistryEntriesDialogVisible.value = true;
}

function closeImport() {
    registryEntriesFile.value = undefined;
    importRegistryEntriesDialogVisible.value = false;
}

async function importRegistryEntriesFile() {
    await withLoadingScreen(async () => {
        const importedRegistryEntries = await registriesAPI.importRegistryEntries(
            registrySchema.value,
            registryEntriesFile.value
        );
        buildTable(importedRegistryEntries);
        closeImport();
    });
}

async function unArchiveRegistryEntry(entry: RegistryEntry) {
    await withLoadingScreen(async () => {
        await registriesAPI.unArchiveRegistryEntry(entry.id);
        await reloadEntries();
    });
}

async function archiveRegistryEntry(entry: RegistryEntry) {
    if (confirm(`Are you sure you want to archive ${entry.displayName}?`)) {
        await withLoadingScreen(async () => {
            await registriesAPI.archiveRegistryEntry(entry.id);
            await reloadEntries();
        });
    }
}

async function deleteRegistryEntry(entry: RegistryEntry) {
    if (confirm(`Are you sure you want to delete ${entry.displayName}?`)) {
        await withLoadingScreen(async () => {
            await registriesAPI.deleteRegistryEntry(entry.id);
            await reloadEntries();
        });
    }
}

</script>
<style>
.archived {
    color: gray;
    background-color: #e9e9e9;
}
</style>
